import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence } from 'motion/react';
import {
  WizardStepContext,
  type WizardModalName,
  type WizardStepValue,
} from '../design/wizardStepContext';
import { ModalShell } from '../design/ModalShell';
import { FormField } from '../design/FormField';
import { SelectControl } from '../design/SelectControl';
import { TagsControl } from '../design/TagsControl';
import { Button } from '../design/Button';
import { ArtefactTypeSelector } from './ArtefactTypeSelector';
import { ArtefactFileInput } from './ArtefactFileInput';
import {
  useArtefactUpload,
  type ArtefactUploadArgs,
  type ArtefactUploadVm,
} from '../useArtefactUpload';

/**
 * ArtefactWizard — the parallel V3 flow for the File/Prompt/Link artefact pipeline (UP-F04/F08).
 *
 * WHY A PARALLEL FLOW. P1 (UploadSelectPage) is a Dataverse FLOW PICKER; the artefact path has no
 * cloud automation, no target environment and no blueprint, so the type selector (UP-F04) cannot be
 * retrofitted onto it. This is the branch UploadOverlayPages switches into when the builder presses
 * P1's "Upload from device". It renders the design's OWN frame — the same portal, inert sweep, Tab
 * cycle and bordered box as SelectFrame — so it is visible on the live V3 skin and reads as the same
 * modal, with the artefact capture where the wizard steps would be. It does NOT reuse the Dataverse
 * useUploadOverlay hook (that requires a flow); it drives the parallel useArtefactUpload hook.
 *
 * TERRITORY. C1 built the type-selector shell, the FILE input, the shared Details step and the submit
 * wiring here. C2 owns the PROMPT and LINK inputs and the GUARDRAIL panel — the "C2:"-marked JSX
 * slots below are where those plug in, reading the same vm this constructs. Keeping the file input
 * and the type selector as their own components lets C2 add its inputs without touching them.
 *
 * SCRIM: like the Dataverse wizard frame, this draws NO scrim — UploadOverlayPages owns the one
 * persistent scrim that spans the P1 → artefact handoff. See that file's persistent-scrim note.
 */
export interface ArtefactWizardProps extends ArtefactUploadArgs {
  /** Back out of the artefact flow to P1 (the design puts both in one box). */
  onBackToSelect: () => void;
}

export function ArtefactWizard({ onBackToSelect, ...args }: ArtefactWizardProps) {
  const vm = useArtefactUpload(args);

  const dialogRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [modalName, setModalName] = useState<WizardModalName>({});
  const stepValue = useMemo<WizardStepValue>(() => ({ setModalName }), [setModalName]);

  const { onClose } = vm;
  const onBackdropClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) onClose();
    },
    [onClose],
  );

  // The SAME focus trap the Dataverse frames run: inert siblings (except the persistent scrim) + a
  // Tab cycle on the stable frame, restoring focus on unmount. Copied deliberately — the box the
  // builder sees must behave identically whichever flow they are in.
  useEffect(() => {
    const dialog = dialogRef.current;
    const overlay = overlayRef.current;
    if (!dialog || !overlay) return;

    const siblings = overlay.parentElement
      ? ([...overlay.parentElement.children] as HTMLElement[]).filter(
          (el) => el !== overlay && !el.hasAttribute('data-overlay-scrim'),
        )
      : [];
    const restore = siblings.map((el) => [el, el.inert] as const);
    siblings.forEach((el) => {
      el.inert = true;
    });

    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialog.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [
        ...dialog.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      restore.forEach(([el, wasInert]) => {
        el.inert = wasInert;
      });
      previouslyFocused?.focus?.();
    };
  }, [onClose]);

  return createPortal(
    <div ref={overlayRef} className="pointer-events-none fixed inset-0 z-50" data-overlay-root>
      {/* Backdrop click-off is a MOUSE convenience; keyboard dismiss is Escape, handled in the
          keydown listener above. Same construction (and same lint stance) as SelectFrame's backdrop. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        onClick={onBackdropClick}
        className="pointer-events-auto absolute inset-x-0 bottom-0 top-header flex items-center justify-center p-modal-x"
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={modalName.labelledBy}
          aria-label={modalName.labelledBy ? undefined : modalName.label}
          tabIndex={-1}
          className={[
            'relative flex w-full max-w-modal flex-col',
            'h-modal max-h-full',
            'rounded-lg border border-ink bg-surface',
          ].join(' ')}
        >
          <WizardStepContext.Provider value={stepValue}>
            <AnimatePresence mode="wait" initial={false}>
              <ArtefactBody
                key="artefact"
                vm={vm}
                onBackToSelect={onBackToSelect}
                onClose={onClose}
              />
            </AnimatePresence>
          </WizardStepContext.Provider>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * The fading contents: the artefact-type selector, the type-specific input, the shared Details form,
 * the attestation gate and the submit footer. Wrapped in ModalShell so it cross-fades inside the
 * stationary frame exactly as the Dataverse steps do.
 */
function ArtefactBody({
  vm,
  onBackToSelect,
  onClose,
}: {
  vm: ArtefactUploadVm;
  onBackToSelect: () => void;
  onClose: () => void;
}) {
  const { details, setDetails, detailErrors, tried, refData } = vm;

  // Adapt the artefact vm's setDetails to the (details, onChange) shape TagsControl expects — the
  // control was written against the Dataverse vm, but it only touches `details` + a patch callback.
  const onDetailsChange = useCallback(
    (patch: Partial<typeof details>) => setDetails((d) => ({ ...d, ...patch })),
    [setDetails],
  );
  const showError = (field: keyof typeof detailErrors) =>
    tried && detailErrors[field] !== undefined;

  return (
    <ModalShell
      label="Upload from device"
      headerInset="title"
      header={
        <div className="flex flex-wrap items-center justify-between gap-7">
          <h1 className="min-w-0 text-xl font-light text-text">Upload a tool</h1>
          <button
            type="button"
            onClick={onBackToSelect}
            className="shrink-0 text-xs font-light text-ink underline underline-offset-2"
          >
            Back to automations
          </button>
        </div>
      }
      footer={
        <div className="border-t border-ink">
          <div className="flex flex-col gap-3 px-modal-x py-11 pr-modal-footer-r">
            {/* UP-F45 pre-press hint — the outstanding required items. The button STAYS enabled;
                clicking with items left surfaces the first offender via onSubmit's re-checks. C2's
                guardrail panel is the richer GC-1..GC-9 view; this inline list is the always-present
                summary beside the button. */}
            {vm.outstandingItems.length > 0 ? (
              <ul className="text-xs font-light text-ink/60">
                {vm.outstandingItems.map((item) => (
                  <li key={item}>• {item}</li>
                ))}
              </ul>
            ) : null}
            {vm.errorMsg ? <p className="text-xs font-light text-error">{vm.errorMsg}</p> : null}
            <div className="flex flex-wrap items-center justify-between gap-7">
              <Button variant="outline-square" size="xs" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="outline-square"
                size="xs"
                onClick={() => void vm.onSubmit()}
                state={vm.submitting ? 'disabled' : 'default'}
              >
                {vm.submitting
                  ? 'Submitting…'
                  : refData.moderationMode === 'self_moderated'
                    ? 'Publish'
                    : 'Submit for review'}
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-7 pb-11 pt-11">
        {/* UP-F04: the artefact-type selector (C1's shell). Switches which input shows (UP-F08). */}
        <ArtefactTypeSelector value={vm.artefactType} onChange={vm.setArtefactType} />

        {/* UP-F08: the type-specific input. File is C1's; Prompt/Link are C2's slots. */}
        {vm.artefactType === 'file' ? (
          <ArtefactFileInput vm={vm} />
        ) : (
          // C2: mount ArtefactPromptInput (artefactType === 'prompt') and ArtefactLinkInput
          // (artefactType === 'link') here — both read/write vm.promptText / vm.linkUrl and their
          // setters. This placeholder keeps the flow usable until C2's inputs land.
          <p className="rounded-md border border-dashed border-border-subtle px-5 py-6 text-xs font-light text-ink/60">
            {vm.artefactType === 'prompt'
              ? 'Prompt input coming from C2.'
              : 'Link input coming from C2.'}
          </p>
        )}

        {/* The shared Details form — the same fields the Dataverse Describe step uses, artefact-
            agnostic. Reused verbatim via FormField / SelectControl / TagsControl. */}
        <div className="grid grid-cols-1 gap-5">
          <FormField
            label="Name*"
            name="artefact-name"
            required
            value={details.name}
            onChange={(v) => onDetailsChange({ name: v })}
            invalid={showError('name')}
            placeholder="What is your tool called?"
          />
          <FormField
            label="Description*"
            name="artefact-description"
            size="tall"
            required
            value={details.description}
            onChange={(v) => onDetailsChange({ description: v })}
            invalid={showError('description')}
            placeholder="What does it do?"
          />
          <FormField
            label="Problem it solves*"
            name="artefact-problem"
            size="tall"
            required
            value={details.problemSolved}
            onChange={(v) => onDetailsChange({ problemSolved: v })}
            invalid={showError('problemSolved')}
            placeholder="Which problem does it solve?"
          />
          <FormField label="Primary category*" name="artefact-category" required>
            <SelectControl
              name="artefact-category"
              value={details.categoryId}
              onChange={(id) => onDetailsChange({ categoryId: id })}
              options={refData.categories}
              invalid={showError('categoryId')}
            />
          </FormField>
          <FormField label="Who may use it*" name="artefact-access" required>
            <SelectControl
              name="artefact-access"
              value={details.accessId}
              onChange={(id) => onDetailsChange({ accessId: id })}
              options={refData.accessRequirements}
              invalid={showError('accessId')}
            />
          </FormField>
          <FormField label="Department*" name="artefact-department" required>
            <SelectControl
              name="artefact-department"
              value={details.departmentId}
              onChange={(id) => onDetailsChange({ departmentId: id })}
              options={refData.departments}
              invalid={showError('departmentId')}
            />
          </FormField>
          <FormField
            label="Time saved per use (minutes)*"
            name="artefact-time"
            type="number"
            required
            value={details.estimatedTimeSaved}
            onChange={(v) => onDetailsChange({ estimatedTimeSaved: v })}
            invalid={showError('estimatedTimeSaved')}
          />
          <FormField
            label="Dependencies*"
            name="artefact-dependencies"
            required
            value={details.dependencies}
            onChange={(v) => onDetailsChange({ dependencies: v })}
            invalid={showError('dependencies')}
            placeholder="What it depends on, or “None”"
          />
          {/* Required on the artefact path (no component-docs projection to render it from). */}
          <FormField
            label="Setup instructions*"
            name="artefact-setup"
            size="tall"
            required
            value={vm.setupInstructions}
            onChange={vm.setSetupInstructions}
            invalid={tried && vm.setupInstructions.trim().length < 10}
            placeholder="How does an adopter set this up?"
          />
          <FormField label="Tags" name="artefact-tags">
            <TagsControl details={details} onChange={onDetailsChange} />
          </FormField>
        </div>

        {/* GC-7: the data-safety attestation — the hard gate the server re-checks. */}
        <label className="flex items-start gap-3 text-xs font-light text-ink">
          <input
            type="checkbox"
            checked={vm.attested}
            onChange={(e) => vm.setAttested(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            I confirm this artefact contains no confidential or personal data that should not be
            shared, and I take responsibility for what I upload.
          </span>
        </label>

        {/* C2: mount GuardrailPanel here (GC-1..GC-9), reading vm.detailErrors + vm.outstandingItems
            + the artefact state. Informative, not a hard gate. */}
      </div>
    </ModalShell>
  );
}
